<script>
  import { createEventDispatcher } from 'svelte';
  import SideSheet from './SideSheet.svelte';
  import ErrorBanner from './ErrorBanner.svelte';
  import InfoHint from './InfoHint.svelte';
  import { bilanciApi } from '../stores/api.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { formatDate, localizedError } from '../lib/labels.js';
  import {
    BILANCIO_ACCEPT, base64FromDataUrl, fileProblem, formatFileSize, formatDecimal,
  } from '../lib/bilancio.js';

  /**
   * Il caricamento di un bilancio in due tempi, in un pannello laterale: il
   * file scelto va prima al server con `dryRun`, che lo legge, lo controlla e
   * dice che cosa farebbe senza salvare niente; solo dopo aver visto
   * l'anteprima si conferma. La stessa strada ripristina un file archiviato
   * che manca o è alterato (`repairFor`): si ricarica lo stesso file.
   *
   * Il server resta l'autorità su tutto: chi può caricare, chi può
   * sostituire, se il file è dell'azienda. Qui si evita solo di offrire un
   * pulsante che verrebbe rifiutato.
   */

  export let open = false;
  export let companyId = '';
  /** Il ruolo può sostituire il bilancio di un esercizio (amministratore). */
  export let canReplace = false;
  /** Il bilancio il cui file va ripristinato, o null per un caricamento nuovo. */
  export let repairFor = null;

  const dispatch = createEventDispatcher();

  let fileInput;
  let file = null;
  let contentBase64 = '';
  let preview = null;
  let checking = false;
  let saving = false;
  let error = null;
  let replaceConfirmed = false;
  let wasOpen = false;

  $: if (open && !wasOpen) reset();
  $: wasOpen = open;

  $: action = preview?.action ?? null;
  $: busy = checking || saving;
  $: groups = Object.entries(preview?.gruppi ?? {})
    .filter(([, status]) => status?.corrente !== 'unknown' || status?.precedente !== 'unknown');
  $: title = repairFor
    ? $t('bilancio.upload.titleRepair', { year: repairFor.fiscalYear })
    : $t('bilancio.upload.title');

  function reset() {
    file = null;
    contentBase64 = '';
    preview = null;
    checking = false;
    saving = false;
    error = null;
    replaceConfirmed = false;
    if (fileInput) fileInput.value = '';
  }

  function onFileChange(event) {
    file = event.currentTarget.files?.[0] ?? null;
    contentBase64 = '';
    preview = null;
    replaceConfirmed = false;
    const problem = fileProblem(file);
    error = file && problem ? localizedError(tImmediate(`bilancio.upload.problems.${problem}`)) : null;
  }

  function readAsBase64(chosen) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(base64FromDataUrl(String(reader.result || '')));
      reader.onerror = () => reject(reader.error || new Error('read failed'));
      reader.readAsDataURL(chosen);
    });
  }

  async function check() {
    const problem = fileProblem(file);
    if (problem) {
      error = localizedError(tImmediate(`bilancio.upload.problems.${problem}`));
      return;
    }
    checking = true;
    error = null;
    try {
      try {
        contentBase64 = await readAsBase64(file);
      } catch {
        throw localizedError(tImmediate('bilancio.upload.problems.read'));
      }
      const response = await bilanciApi.upload(companyId, { contentBase64, filename: file.name, dryRun: true });
      preview = response.preview ?? null;
    } catch (err) {
      error = err;
      preview = null;
    } finally {
      checking = false;
    }
  }

  async function save() {
    if (!preview || !contentBase64) return;
    const replace = action === 'replace';
    if (replace && !(canReplace && replaceConfirmed)) return;
    saving = true;
    error = null;
    try {
      const response = await bilanciApi.upload(companyId, { contentBase64, filename: file.name, replace });
      dispatch('done', { outcome: response.outcome, bilancio: response.bilancio, replaced: Boolean(response.replacedId) });
    } catch (err) {
      error = err;
    } finally {
      saving = false;
    }
  }

  function chooseAnother() {
    reset();
    fileInput?.focus();
  }

  function close() {
    if (saving) return;
    dispatch('close');
  }

  const period = (start, end) => (start && end
    ? $t('bilancio.list.period', { start: formatDate(start), end: formatDate(end) })
    : '');
</script>

<SideSheet {open} id="bilancio-upload" eyebrow={$t('bilancio.upload.eyebrow')} {title}
  closeLabel={$t('bilancio.upload.close')} initialFocus="#bilancio-file" on:close={close}>
  <p class="sheet-section-intro">
    {repairFor ? $t('bilancio.upload.introRepair', { year: repairFor.fiscalYear }) : $t('bilancio.upload.intro')}
  </p>

  <ErrorBanner {error} title={error && !error.localized ? $t('bilancio.upload.errorTitle') : ''} />

  <section class="sheet-section" aria-labelledby="bilancio-file-label">
    <label id="bilancio-file-label" class="label" for="bilancio-file">{$t('bilancio.upload.fileLabel')}</label>
    <input id="bilancio-file" bind:this={fileInput} type="file" accept={BILANCIO_ACCEPT} class="input w-full"
      aria-describedby="bilancio-file-hint" disabled={busy} on:change={onFileChange} />
    <p id="bilancio-file-hint" class="field-hint">{$t('bilancio.upload.fileHint')}</p>
    {#if file}
      <p class="mt-2 text-xs text-muted break-words">
        {$t('bilancio.upload.chosen', { name: file.name, size: formatFileSize(file.size) })}
      </p>
    {/if}
  </section>

  {#if preview}
    <section class="sheet-section" aria-labelledby="bilancio-preview-title" aria-live="polite">
      <h3 id="bilancio-preview-title" class="sheet-section-title">{$t('bilancio.upload.preview.heading')}</h3>

      <dl class="bilancio-preview">
        <div>
          <dt>{$t('bilancio.upload.preview.azienda')}</dt>
          <dd>{preview.denominazione || '—'}</dd>
        </div>
        <div>
          <dt>{$t('bilancio.upload.preview.codice')}</dt>
          <dd class="mono">{preview.entityIdentifier}</dd>
        </div>
        <div>
          <dt>{$t('bilancio.upload.preview.esercizio')}</dt>
          <dd>{preview.fiscalYear} · {period(preview.periodStart, preview.periodEnd)}</dd>
        </div>
        <div>
          <dt>{$t('bilancio.upload.preview.precedente')}</dt>
          <dd>{preview.prevPeriodEnd ? period(preview.prevPeriodStart, preview.prevPeriodEnd) : $t('bilancio.upload.preview.noPrevious')}</dd>
        </div>
        <div>
          <dt>{$t('bilancio.upload.preview.forma')}</dt>
          <dd class="inline-flex items-center gap-1">
            {$t(`bilancio.forma.${preview.schemaType}`)}
            <InfoHint text={$t(`bilancio.formaHelp.${preview.schemaType}`)} />
          </dd>
        </div>
        <div>
          <dt>{$t('bilancio.upload.preview.fatti')}</dt>
          <dd>{formatDecimal(String(preview.factsCount ?? 0))}</dd>
        </div>
        {#if preview.stats?.duplicatesConsolidated > 0}
          <div>
            <dt>{$t('bilancio.upload.preview.duplicati')}</dt>
            <dd>{preview.stats.duplicatesConsolidated}</dd>
          </div>
        {/if}
        <div>
          <dt>{$t('bilancio.upload.preview.file')}</dt>
          <dd class="break-words">{preview.originalFilename} · {formatFileSize(preview.fileSize)}</dd>
        </div>
        <div>
          <dt>{$t('bilancio.upload.preview.tassonomia')}</dt>
          <dd class="mono">PCI {preview.taxonomyVersion}</dd>
        </div>
      </dl>

      {#if action}
        {@const notAllowed = action === 'replace' && !canReplace}
        <div class="alert mt-4 {action === 'replace' ? 'alert-warning' : action === 'restore' ? 'alert-accent' : 'alert-info'}" role="status">
          <div class="min-w-0 flex-1">
            <p class="alert-message">
              {notAllowed ? $t('bilancio.upload.preview.action.replaceNotAllowed') : $t(`bilancio.upload.preview.action.${action}`)}
            </p>
            {#if preview.existing?.uploadedAt}
              <p class="mt-1 text-xs text-muted">{$t('bilancio.upload.preview.existing', { date: formatDate(preview.existing.uploadedAt) })}</p>
            {/if}
          </div>
        </div>
        {#if action === 'replace' && canReplace}
          <label class="mt-3 flex items-start gap-2 text-sm">
            <input type="checkbox" class="mt-1" bind:checked={replaceConfirmed} disabled={busy} />
            <span>{$t('bilancio.upload.preview.replaceConfirm')}</span>
          </label>
        {/if}
      {/if}
    </section>

    <section class="sheet-section" aria-labelledby="bilancio-preview-warnings">
      <h3 id="bilancio-preview-warnings" class="sheet-section-title">{$t('bilancio.upload.preview.warningsHeading')}</h3>
      {#if preview.warnings?.length}
        <ul class="bilancio-bullets">
          {#each preview.warnings as warning}
            <li>{warning.message}</li>
          {/each}
        </ul>
      {:else}
        <p class="sheet-section-intro">{$t('bilancio.upload.preview.noWarnings')}</p>
      {/if}
    </section>

    {#if groups.length}
      <section class="sheet-section" aria-labelledby="bilancio-preview-groups">
        <h3 id="bilancio-preview-groups" class="sheet-section-title">{$t('bilancio.upload.preview.groupsHeading')}</h3>
        <p class="sheet-section-intro">{$t('bilancio.upload.preview.groupsIntro')}</p>
        <ul class="bilancio-bullets">
          {#each groups as [concept, status]}
            <li>
              <span class="text-heading">{$t(`bilancio.gruppi.${concept}`, undefined, concept)}</span>
              <span class="block text-xs text-muted">
                {[
                  $t('bilancio.upload.preview.groupYear', { year: preview.fiscalYear, status: $t(`bilancio.upload.preview.groupStatus.${status.corrente}`) }),
                  preview.prevPeriodEnd
                    ? $t('bilancio.upload.preview.groupYear', { year: preview.fiscalYear - 1, status: $t(`bilancio.upload.preview.groupStatus.${status.precedente}`) })
                    : '',
                ].filter(Boolean).join(' · ')}
              </span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}
  {/if}

  <svelte:fragment slot="footer">
    {#if !preview}
      <button type="button" class="btn btn-secondary" on:click={close} disabled={saving}>{$t('bilancio.upload.cancel')}</button>
      <button type="button" class="btn btn-primary" on:click={check} disabled={!file || busy}>
        {checking ? $t('bilancio.upload.checking') : $t('bilancio.upload.check')}
      </button>
    {:else}
      <button type="button" class="btn btn-secondary" on:click={chooseAnother} disabled={busy}>{$t('bilancio.upload.chooseAnother')}</button>
      {#if action === 'create'}
        <button type="button" class="btn btn-primary" on:click={save} disabled={busy}>
          {saving ? $t('bilancio.upload.saving') : $t('bilancio.upload.save')}
        </button>
      {:else if action === 'restore'}
        <button type="button" class="btn btn-primary" on:click={save} disabled={busy}>
          {saving ? $t('bilancio.upload.saving') : $t('bilancio.upload.restore')}
        </button>
      {:else if action === 'replace' && canReplace}
        <button type="button" class="btn btn-primary" on:click={save} disabled={busy || !replaceConfirmed}>
          {saving ? $t('bilancio.upload.saving') : $t('bilancio.upload.replace')}
        </button>
      {:else}
        <button type="button" class="btn btn-primary" on:click={close}>{$t('bilancio.upload.close')}</button>
      {/if}
    {/if}
  </svelte:fragment>
</SideSheet>

<style>
  .bilancio-preview {
    display: grid;
    gap: 10px 18px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 13rem), 1fr));
  }
  .bilancio-preview dt {
    color: var(--color-text-muted);
    font-size: 0.75rem;
  }
  .bilancio-preview dd {
    margin: 2px 0 0;
    color: var(--color-text-heading);
    overflow-wrap: anywhere;
  }
  .bilancio-bullets {
    padding-left: 1.1rem;
    list-style: disc;
    font-size: 0.875rem;
  }
  .bilancio-bullets li + li { margin-top: 6px; }
</style>
